import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import { statementTemplateService } from '../../services/api/statementTemplateService';
import { statementSourceTagService } from '../../services/api/statementSourceTagService';

const StatementTemplateBuilder = ({ templateId, readOnly = false, onSave, onCancel }) => {
  const [template, setTemplate] = useState({
    template_id: '',
    name: '',
    description: '',
    config: {
      sections: []
    }
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
  const [newSection, setNewSection] = useState({ title: '', description: '' });
  const [newField, setNewField] = useState({ tag_id: '', required: false });

  useEffect(() => {
    loadAvailableTags();
    if (templateId) {
      loadTemplate();
    }
  }, [templateId]);

  const loadAvailableTags = async () => {
    try {
      const response = await statementSourceTagService.listSourceTags();
      setAvailableTags(response.tags || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError('Failed to load available tags');
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await statementTemplateService.getTemplate(templateId);
      setTemplate(response);
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = () => {
    setNewSection({ title: '', description: '' });
    setSectionDialogOpen(true);
  };

  const handleSaveSection = () => {
    if (!newSection.title) {
      setError('Section title is required');
      return;
    }

    const updatedSections = [...template.config.sections, {
      ...newSection,
      fields: []
    }];

    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });

    setSectionDialogOpen(false);
    setNewSection({ title: '', description: '' });
  };

  const handleRemoveSection = (index) => {
    const updatedSections = template.config.sections.filter((_, i) => i !== index);
    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleMoveSectionUp = (index) => {
    if (index === 0) return;
    const updatedSections = [...template.config.sections];
    [updatedSections[index - 1], updatedSections[index]] =
      [updatedSections[index], updatedSections[index - 1]];
    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleMoveSectionDown = (index) => {
    if (index === template.config.sections.length - 1) return;
    const updatedSections = [...template.config.sections];
    [updatedSections[index], updatedSections[index + 1]] =
      [updatedSections[index + 1], updatedSections[index]];
    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleAddField = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setNewField({ tag_id: '', required: false });
    setFieldDialogOpen(true);
  };

  const handleSaveField = () => {
    if (!newField.tag_id) {
      setError('Please select a field tag');
      return;
    }

    const updatedSections = [...template.config.sections];
    updatedSections[currentSectionIndex].fields.push(newField);

    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });

    setFieldDialogOpen(false);
    setNewField({ tag_id: '', required: false });
    setCurrentSectionIndex(null);
  };

  const handleRemoveField = (sectionIndex, fieldIndex) => {
    const updatedSections = [...template.config.sections];
    updatedSections[sectionIndex].fields = updatedSections[sectionIndex].fields.filter(
      (_, i) => i !== fieldIndex
    );

    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleMoveFieldUp = (sectionIndex, fieldIndex) => {
    if (fieldIndex === 0) return;
    const updatedSections = [...template.config.sections];
    const fields = updatedSections[sectionIndex].fields;
    [fields[fieldIndex - 1], fields[fieldIndex]] = [fields[fieldIndex], fields[fieldIndex - 1]];
    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleMoveFieldDown = (sectionIndex, fieldIndex) => {
    const section = template.config.sections[sectionIndex];
    if (fieldIndex === section.fields.length - 1) return;
    const updatedSections = [...template.config.sections];
    const fields = updatedSections[sectionIndex].fields;
    [fields[fieldIndex], fields[fieldIndex + 1]] = [fields[fieldIndex + 1], fields[fieldIndex]];
    setTemplate({
      ...template,
      config: {
        ...template.config,
        sections: updatedSections
      }
    });
  };

  const handleSaveTemplate = async () => {
    if (!template.template_id || !template.name) {
      setError('Template ID and name are required');
      return;
    }

    if (template.config.sections.length === 0) {
      setError('Template must have at least one section');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (templateId) {
        await statementTemplateService.updateTemplate(templateId, template);
      } else {
        await statementTemplateService.createTemplate(template);
      }

      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const getTagById = (tagId) => {
    return availableTags.find(tag => tag.tag_id === tagId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Scrollable content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', pb: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {readOnly ? 'View Template' : templateId ? 'Edit Template' : 'Create New Template'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Template ID"
              value={template.template_id}
              onChange={(e) => setTemplate({ ...template, template_id: e.target.value })}
              disabled={!!templateId || readOnly}
              sx={{ mb: 2 }}
              helperText="Unique identifier (e.g., retail-theft-v1)"
            />

            <TextField
              fullWidth
              label="Template Name"
              value={template.name}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              disabled={readOnly}
              sx={{ mb: 2 }}
              helperText="Display name (e.g., Retail Theft Statement)"
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={template.description || ''}
              onChange={(e) => setTemplate({ ...template, description: e.target.value })}
              disabled={readOnly}
              sx={{ mb: 2 }}
              helperText="Optional description of this template"
            />
          </CardContent>
        </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Sections</Typography>
        {!readOnly && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddSection}
          >
            Add Section
          </Button>
        )}
      </Box>

      {template.config.sections.map((section, sectionIndex) => (
        <Card key={sectionIndex} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">{section.title}</Typography>
                {section.description && (
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                )}
              </Box>
              {!readOnly && (
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveSectionUp(sectionIndex)}
                    disabled={sectionIndex === 0}
                  >
                    <ArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveSectionDown(sectionIndex)}
                    disabled={sectionIndex === template.config.sections.length - 1}
                  >
                    <ArrowDownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveSection(sectionIndex)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <List dense>
              {section.fields.map((field, fieldIndex) => {
                const tag = getTagById(field.tag_id);
                return (
                  <ListItem
                    key={fieldIndex}
                    secondaryAction={
                      !readOnly && (
                        <Box>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleMoveFieldUp(sectionIndex, fieldIndex)}
                            disabled={fieldIndex === 0}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleMoveFieldDown(sectionIndex, fieldIndex)}
                            disabled={fieldIndex === section.fields.length - 1}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveField(sectionIndex, fieldIndex)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{tag?.label || field.tag_id}</span>
                          {field.required && <Chip label="Required" size="small" color="primary" />}
                          <Chip label={tag?.source || 'unknown'} size="small" variant="outlined" />
                        </Box>
                      }
                      secondary={tag?.description || field.tag_id}
                    />
                  </ListItem>
                );
              })}
            </List>

            {!readOnly && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddField(sectionIndex)}
                sx={{ mt: 1 }}
              >
                Add Field
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      {template.config.sections.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              No sections yet. Click "Add Section" to get started.
            </Typography>
          </CardContent>
        </Card>
      )}
      </Box>

      {/* Sticky button bar at bottom */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          flexShrink: 0
        }}
      >
        <Button onClick={onCancel}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={loading}
          >
            {loading ? 'Saving...' : templateId ? 'Update Template' : 'Create Template'}
          </Button>
        )}
      </Box>

      {/* Section Dialog */}
      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Section</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Section Title"
            value={newSection.title}
            onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description (optional)"
            value={newSection.description}
            onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSection} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Field</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Field Tag</InputLabel>
            <Select
              value={newField.tag_id}
              onChange={(e) => setNewField({ ...newField, tag_id: e.target.value })}
              label="Field Tag"
            >
              {availableTags.map((tag) => (
                <MenuItem key={tag.tag_id} value={tag.tag_id}>
                  <Box>
                    <Typography variant="body1">{tag.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tag.tag_id} • {tag.source} • {tag.data_type}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {newField.tag_id && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {getTagById(newField.tag_id)?.description}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={newField.required}
                onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
              />
            }
            label="Required field"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveField} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(StatementTemplateBuilder);
